// Top-level (workspace-scoped) memory + activity + feedback client.
//
// Per-project memory lives in ./projects.ts; this module is the
// workspace-level memory surface (`/api/memory/*`) plus a couple of
// adjacent small concerns. Mirrors the backend's routes/memory.py +
// routes/activity.py + routes/feedback.py.

import {
  API,
  encodePath,
  requestJson,
  requestOk,
  type ActivityEntry,
  type MemoryDoc,
  type MemoryPreview,
} from "../api";


// ---------------------------------------------------------------------------
// Workspace memory (admin-curated; project memory lives in ./projects.ts)
// ---------------------------------------------------------------------------

export async function listMemory(): Promise<MemoryPreview[]> {
  return requestJson(`${API}/api/memory`);
}

export async function readMemory(topic: string): Promise<MemoryDoc> {
  return requestJson(`${API}/api/memory/${encodePath(topic)}`);
}

/** Read a workspace memory topic; 404 → null ("topic not set yet"). */
export async function readMemoryOptional(
  topic: string,
  signal?: AbortSignal,
): Promise<MemoryDoc | null> {
  const r = await requestOk(
    `${API}/api/memory/${encodePath(topic)}`,
    { signal },
    { allowStatus: [404] },
  );
  if (r.status === 404) return null;
  return r.json() as Promise<MemoryDoc>;
}

export async function writeMemory(topic: string, body: string): Promise<void> {
  await requestOk(`${API}/api/memory/${encodePath(topic)}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ body }),
  });
}

export async function deleteMemory(topic: string): Promise<void> {
  await requestOk(`${API}/api/memory/${encodePath(topic)}`, { method: "DELETE" });
}

export async function renameMemory(topic: string, newTopic: string): Promise<{ topic: string; path: string }> {
  return requestJson(`${API}/api/memory/${encodePath(topic)}/rename`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ new_topic: newTopic }),
  });
}


// ---------------------------------------------------------------------------
// Activity log (workspace-scoped feed of memory writes / promotions /
// notable events). Small enough to ride with the memory client.
// ---------------------------------------------------------------------------

export async function listActivity(limit = 50): Promise<ActivityEntry[]> {
  return requestJson(`${API}/api/activity?limit=${limit}`);
}


// ---------------------------------------------------------------------------
// Feedback (bug reports / suggestions — files a GitHub issue server-side)
// ---------------------------------------------------------------------------

export async function submitFeedback(
  description: string,
  taskId?: string | null,
): Promise<{ issue_url: string; issue_id: string }> {
  return requestJson(`${API}/api/feedback`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ description, task_id: taskId ?? null }),
  });
}
